import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { UserFavorite, Product } from '../types';
import toast from 'react-hot-toast';
import { getFavorites, addToFavorites as apiAddToFavorites, removeFromFavorites as apiRemoveFromFavorites, removeProductFromFavorites } from '../utils/api';
import { AuthContext } from './AuthContext';

interface FavoritesContextType {
  favorites: UserFavorite[];
  loading: boolean;
  addToFavorites: (product: Product) => Promise<void>;
  removeFromFavorites: (favoriteId: number) => Promise<void>;
  removeProductFromFavorites: (productId: number) => Promise<void>;
  isFavorite: (productId: number) => boolean;
  getFavoriteByProductId: (productId: number) => UserFavorite | undefined;
  refreshFavorites: () => Promise<void>;
}

export const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<UserFavorite[]>([]);
  const [loading, setLoading] = useState(false);
  const authContext = useContext(AuthContext);

  // Load favorites from API
  const loadFavorites = useCallback(async () => {
    if (!authContext?.user) {
      setFavorites([]);
      return;
    }

    try {
      setLoading(true);
      const favoritesData = await getFavorites();
      setFavorites(favoritesData);
    } catch (error) {
      console.error("Failed to load favorites", error);
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  }, [authContext?.user]);

  // Load favorites when user changes
  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const addToFavorites = useCallback(async (product: Product) => {
    if (!authContext?.user) {
      toast.error('Please log in to add favorites');
      return;
    }

    try {
      const newFavorite = await apiAddToFavorites(product);
      setFavorites(prev => [newFavorite, ...prev]);
      toast.success(`Added ${product.name} to favorites!`);
    } catch (error: any) {
      console.error('Failed to add to favorites:', error);
      if (error.response?.status === 400) {
        toast.error('Product is already in your favorites');
      } else {
        toast.error('Failed to add to favorites');
      }
    }
  }, [authContext?.user]);

  const removeFromFavorites = useCallback(async (favoriteId: number) => {
    if (!authContext?.user) return;

    try {
      await apiRemoveFromFavorites(favoriteId);
      setFavorites(prev => prev.filter(fav => fav.id !== favoriteId));
      toast.success('Removed from favorites');
    } catch (error) {
      console.error('Failed to remove from favorites:', error);
      toast.error('Failed to remove from favorites');
    }
  }, [authContext?.user]);

  const removeProductFromFavoritesHandler = useCallback(async (productId: number) => {
    if (!authContext?.user) return;

    try {
      await removeProductFromFavorites(productId);
      setFavorites(prev => prev.filter(fav => fav.monorepo_reusable_type_id !== productId));
      toast.success('Removed from favorites');
    } catch (error) {
      console.error('Failed to remove from favorites:', error);
      toast.error('Failed to remove from favorites');
    }
  }, [authContext?.user]);

  const isFavorite = useCallback((productId: number): boolean => {
    return favorites.some(fav => fav.monorepo_reusable_type_id === productId);
  }, [favorites]);

  const getFavoriteByProductId = useCallback((productId: number): UserFavorite | undefined => {
    return favorites.find(fav => fav.monorepo_reusable_type_id === productId);
  }, [favorites]);

  const refreshFavorites = useCallback(async () => {
    await loadFavorites();
  }, [loadFavorites]);

  const value = {
    favorites,
    loading,
    addToFavorites,
    removeFromFavorites,
    removeProductFromFavorites: removeProductFromFavoritesHandler,
    isFavorite,
    getFavoriteByProductId,
    refreshFavorites,
  };

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
